/**
 * The builder's top bar (name, data mode, undo, screen, dates, panels,
 * preview, present, save state), its banners, undo keys and the page setup panel.
 */

import { useEffect, type ReactNode } from 'react';

import { DateControl } from './DateControl.tsx';
import type { SaveState, useHistory } from './hooks.ts';
import type { DocumentSettings } from './reports-api.ts';

export type Panel =
  | 'library'
  | 'settings'
  | 'versions'
  | 'setup'
  | 'theme'
  | 'comments'
  | 'share'
  | 'exports'
  | 'email'
  | null;

export const DEFAULT_SETTINGS: Required<
  Pick<DocumentSettings, 'range' | 'compare' | 'timezone' | 'mode'>
> = {
  range: { preset: 'LAST_MONTH' },
  compare: { mode: 'PREVIOUS_PERIOD' },
  timezone: 'Africa/Johannesburg',
  mode: 'LIVE',
};

const SAVE_LABEL: Record<SaveState, string> = {
  saved: 'All changes saved',
  unsaved: 'Unsaved changes',
  saving: 'Saving…',
  conflict: 'Someone else changed this',
  error: 'Not saved: check your connection',
};

interface BarProps {
  noun: string;
  name: string;
  onName: (name: string) => void;
  settings: DocumentSettings;
  onSettings: (settings: DocumentSettings) => void;
  history: ReturnType<typeof useHistory>;
  isReport: boolean;
  device: 'desktop' | 'mobile';
  onDevice: (device: 'desktop' | 'mobile') => void;
  panel: Panel;
  onPanel: (panel: Panel) => void;
  preview: boolean;
  onPreview: (preview: boolean) => void;
  onPresent: () => void;
  /** Whether the client sees it in their portal; null when it is not a client's document. */
  shared: boolean | null;
  onShare: (shared: boolean) => void;
  onClose: () => void;
  save: SaveState;
}

function Toggle({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: [string, string][];
  value: string;
  onChange: (value: string) => void;
}): ReactNode {
  return (
    <div className="rb-seg" role="group" aria-label={label}>
      {options.map(([key, text]) => (
        <button
          type="button"
          key={key}
          className={value === key ? 'on' : ''}
          aria-pressed={value === key}
          onClick={() => onChange(key)}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

export function BuilderBar(props: BarProps): ReactNode {
  const { noun, settings, onSettings, history, panel, onPanel } = props;
  const toggle = (key: Panel) => onPanel(panel === key ? null : key);
  return (
    <header className="rb-bar">
      <button
        type="button"
        className="rb-icon"
        aria-label={`Back to ${noun}s`}
        onClick={props.onClose}
      >
        ←
      </button>
      <input
        className="rb-name"
        aria-label={`${noun} name`}
        value={props.name}
        maxLength={160}
        onChange={(event) => props.onName(event.target.value)}
      />
      <Toggle
        label="Data"
        options={[
          ['LIVE', 'Live data'],
          ['SAMPLE', 'Sample data'],
        ]}
        value={settings.mode === 'SAMPLE' ? 'SAMPLE' : 'LIVE'}
        onChange={(mode) => onSettings({ ...settings, mode: mode as 'LIVE' | 'SAMPLE' })}
      />
      <button
        type="button"
        className="rb-icon"
        aria-label="Undo"
        title="Undo (⌘Z)"
        disabled={!history.canUndo}
        onClick={history.undo}
      >
        ↶
      </button>
      <button
        type="button"
        className="rb-icon"
        aria-label="Redo"
        title="Redo (⇧⌘Z)"
        disabled={!history.canRedo}
        onClick={history.redo}
      >
        ↷
      </button>
      {!props.isReport && (
        <Toggle
          label="Screen"
          options={[
            ['desktop', 'Desktop'],
            ['mobile', 'Mobile'],
          ]}
          value={props.device}
          onChange={(device) => props.onDevice(device as 'desktop' | 'mobile')}
        />
      )}
      <span className="rb-spacer" />
      <DateControl
        range={settings.range ?? DEFAULT_SETTINGS.range}
        onRange={(range) => onSettings({ ...settings, range })}
        compare={settings.compare ?? DEFAULT_SETTINGS.compare}
        onCompare={(compare) => onSettings({ ...settings, compare })}
      />
      <button
        type="button"
        className={`btn small secondary${panel === 'setup' ? ' on' : ''}`}
        onClick={() => toggle('setup')}
      >
        Page setup
      </button>
      <button
        type="button"
        className={`btn small secondary${panel === 'theme' ? ' on' : ''}`}
        onClick={() => toggle('theme')}
      >
        Theme
      </button>
      <button
        type="button"
        className={`btn small secondary${panel === 'versions' ? ' on' : ''}`}
        onClick={() => toggle('versions')}
      >
        History
      </button>
      <button
        type="button"
        className="btn small secondary"
        aria-pressed={props.preview}
        onClick={() => props.onPreview(!props.preview)}
      >
        {props.preview ? 'Edit' : 'Preview'}
      </button>
      {props.shared !== null && (
        <label className="rb-share" title="When on, the client sees this in their portal">
          <input
            type="checkbox"
            role="switch"
            checked={props.shared}
            onChange={(event) => props.onShare(event.target.checked)}
          />
          Share with client
        </label>
      )}
      <button
        type="button"
        className={`btn small secondary${panel === 'email' ? ' on' : ''}`}
        onClick={() => toggle('email')}
      >
        Email
      </button>
      <button
        type="button"
        className={`btn small secondary${panel === 'exports' ? ' on' : ''}`}
        onClick={() => toggle('exports')}
      >
        Download PDF
      </button>
      <button
        type="button"
        className={`btn small secondary${panel === 'share' ? ' on' : ''}`}
        onClick={() => toggle('share')}
      >
        Share link
      </button>
      {props.isReport && (
        <button
          type="button"
          className={`btn small secondary${panel === 'comments' ? ' on' : ''}`}
          onClick={() => toggle('comments')}
        >
          Comments
        </button>
      )}
      {props.isReport && (
        <button type="button" className="btn small" onClick={props.onPresent}>
          ▶ Present
        </button>
      )}
      <span className={`rb-save rb-save-${props.save}`} role="status" aria-live="polite">
        {SAVE_LABEL[props.save]}
      </span>
    </header>
  );
}

/** Shown when the builder opens a share link's frozen copy rather than the original. */
export const SHARE_COPY_NOTICE =
  'You are editing a share link’s copy. Changes show on that link only.';

export function BuilderBanners({
  noun,
  save,
  error,
  notice,
  onDismiss,
}: {
  noun: string;
  save: SaveState;
  error: string | null;
  notice: string | null;
  onDismiss: () => void;
}): ReactNode {
  return (
    <>
      {save === 'conflict' && (
        <div className="rb-banner" role="alert">
          Someone saved this {noun} after you opened it, so your latest edits were not saved.
          <button type="button" className="btn small" onClick={() => window.location.reload()}>
            Reload the latest version
          </button>
        </div>
      )}
      {error && (
        <div className="rb-banner" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="rb-banner" role="status">
          {notice}
          <button type="button" className="btn small secondary" onClick={onDismiss}>
            OK
          </button>
        </div>
      )}
    </>
  );
}

/** ⌘Z undoes, ⇧⌘Z redoes, except while typing in a field. */
export function useUndoKeys(history: { undo: () => void; redo: () => void }): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const typing = (event.target as HTMLElement)?.closest('input, textarea, select');
      if (typing || !(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') return;
      event.preventDefault();
      if (event.shiftKey) history.redo();
      else history.undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [history]);
}
